import { PROVIDER_LABELS_TH, type Provider } from '@natashu/shared';
import { useQueryClient } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router';
import { useSwipeAway } from '../../lib/useSwipeAway.ts';
import { ProviderIcon } from '../../components/ui/ProviderIcon.tsx';
import { api } from '../../lib/api.ts';
import { useAuth } from '../../lib/auth.tsx';
import { useLatest } from '../../lib/hooks.ts';
import { useRealtimeEvent, type RealtimeEvent } from '../../lib/realtime.ts';
import { inboxKeys } from '../inbox/api.ts';
import { READ_CONVERSATIONS } from '../inbox/counts.ts';
import type { ConversationDetail } from '../inbox/types.ts';
import { claimAlert } from './claim.ts';
import { useChatAlertPrefs } from './prefs.ts';
import { installAudioUnlock, playChime } from './sound.ts';
import './chat-alerts.css';

/** `message.created` payload for provider messages (apps/server/src/modules/webhooks/service.ts). */
interface MessagePayload {
  messageId?: string;
  direction?: string;
  senderType?: string;
  accountPurpose?: string;
  provider?: string;
  preview?: string | null;
  hasImage?: boolean;
  isEcho?: boolean;
}

export interface ChatAlert {
  conversationId: string;
  provider: string;
  providerLabel: string;
  customerName: string;
  accountName: string;
  isDemo: boolean;
  preview: string;
  at: string;
  /** Messages collapsed into this card. */
  count: number;
  /** Bumped on every new message (restarts the auto-dismiss timer). */
  seq: number;
}

const MAX_VISIBLE = 3;
const MAX_KEPT = 20;
// 2 s (owner, 5 ต.ค. 2569: "2 วิหายไปเลย ไม่ต้องแสดงค้าง"). It was 12 s; hovering the card still holds it open.
const DISMISS_MS = 2_000;
/** Replayed events after a long disconnect are not "new" any more. */
const MAX_EVENT_AGE_MS = 2 * 60_000;
const SUMMARY_TTL_MS = 60_000;

export function isCustomerInbound(event: Pick<RealtimeEvent, 'type' | 'conversationId' | 'payload'>): boolean {
  const p = event.payload as MessagePayload;
  return event.type === 'message.created' && Boolean(event.conversationId) && p.direction === 'inbound' && p.senderType === 'customer' && !p.isEcho && p.accountPurpose !== 'admin_notify';
}

export function previewText(p: Pick<MessagePayload, 'preview' | 'hasImage'>): string {
  const text = p.preview?.trim();
  if (text) return text;
  return p.hasImage ? 'ส่งรูปภาพ' : 'ส่งข้อความใหม่';
}

/** Newest first; a new message for a chat already shown updates that card instead of adding one. */
export function mergeAlert(list: readonly ChatAlert[], next: Omit<ChatAlert, 'count' | 'seq'>, seq: number): ChatAlert[] {
  const existing = list.find((a) => a.conversationId === next.conversationId);
  return [{ ...next, count: (existing?.count ?? 0) + 1, seq }, ...list.filter((a) => a.conversationId !== next.conversationId)].slice(0, MAX_KEPT);
}

function openConversationId(pathname: string): string | null {
  const [, section, id] = pathname.split('/');
  return section === 'inbox' && id ? id : null;
}

const timeFormat = new Intl.DateTimeFormat('th-TH', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Bangkok' });

function subscribeVisibility(cb: () => void): () => void {
  document.addEventListener('visibilitychange', cb);
  return () => document.removeEventListener('visibilitychange', cb);
}
const getVisible = () => document.visibilityState === 'visible';

function showDesktopNotification(alert: ChatAlert, onOpen: () => void): void {
  if (typeof window === 'undefined' || typeof window.Notification !== 'function' || window.Notification.permission !== 'granted') return;
  try {
    const n = new Notification(`${alert.customerName} · ${alert.providerLabel}`, {
      body: `${alert.preview}\n${alert.accountName}`,
      // One notification per chat: a burst replaces the previous one.
      tag: `natashu-chat-${alert.conversationId}`,
      lang: 'th',
    });
    n.onclick = () => {
      window.focus();
      onOpen();
      n.close();
    };
  } catch {
    // Some mobile browsers only allow notifications from a service worker; the in-app card still shows.
  }
}

type Summary = Pick<ChatAlert, 'provider' | 'providerLabel' | 'customerName' | 'accountName' | 'isDemo'> & { muted: boolean; snoozed: boolean };

/**
 * New inbound chat alerts: in-app cards (grouped per chat), a short sound and a browser notification
 * while the tab is hidden. Realtime events are already filtered by chat visibility on the server.
 */
export function ChatAlerts() {
  const { can } = useAuth();
  const allowed = can(READ_CONVERSATIONS);
  const { prefs } = useChatAlertPrefs();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const visible = useSyncExternalStore(subscribeVisibility, getVisible, getVisible);
  const [alerts, setAlerts] = useState<ChatAlert[]>([]);
  const seqRef = useRef(0);
  const summaries = useRef(new Map<string, { at: number; summary: Summary }>());
  const latest = useLatest({ prefs, pathname: location.pathname, allowed });

  useEffect(() => installAudioUnlock(), []);

  // Opening a chat clears its card.
  const openId = openConversationId(location.pathname);
  useEffect(() => {
    if (openId) setAlerts((list) => (list.some((a) => a.conversationId === openId) ? list.filter((a) => a.conversationId !== openId) : list));
  }, [openId]);

  const open = useCallback(
    (conversationId: string) => {
      setAlerts((list) => list.filter((a) => a.conversationId !== conversationId));
      navigate(`/inbox/${conversationId}`);
    },
    [navigate],
  );

  const summaryOf = useCallback(
    async (conversationId: string): Promise<Summary | null> => {
      const hit = summaries.current.get(conversationId);
      if (hit && Date.now() - hit.at < SUMMARY_TTL_MS) return hit.summary;
      try {
        const d = await queryClient.fetchQuery({
          queryKey: inboxKeys.detail(conversationId),
          queryFn: ({ signal }) => api.get<ConversationDetail>(`/api/conversations/${conversationId}`, { signal }),
          staleTime: 2_000,
        });
        const now = Date.now();
        const summary: Summary = {
          provider: d.channel.provider,
          providerLabel: PROVIDER_LABELS_TH[d.channel.provider as Provider] ?? d.channel.provider,
          customerName: d.contact?.displayName || d.conversation.contact.displayName || 'ลูกค้า',
          accountName: d.channel.name,
          isDemo: d.channel.isDemo || d.channel.provider === 'mock',
          muted: d.conversation.isMuted || (d.conversation.mutedUntil != null && new Date(d.conversation.mutedUntil).getTime() > now),
          snoozed: d.conversation.isSnoozed || (d.conversation.snoozedUntil != null && new Date(d.conversation.snoozedUntil).getTime() > now),
        };
        summaries.current.set(conversationId, { at: now, summary });
        return summary;
      } catch {
        return null; // no longer visible / deleted
      }
    },
    [queryClient],
  );

  // Mute/snooze/assignment changes: re-read the chat on its next message.
  useRealtimeEvent('conversation.updated', (event) => {
    if (event.conversationId) summaries.current.delete(event.conversationId);
  });

  useRealtimeEvent('message.created', (event) => {
    const { pathname, allowed: ok } = latest.current;
    if (!ok || !isCustomerInbound(event)) return;
    if (Date.now() - new Date(event.at).getTime() > MAX_EVENT_AGE_MS) return;
    const conversationId = event.conversationId!;
    const payload = event.payload as MessagePayload;
    const messageId = payload.messageId ?? event.entityId ?? String(event.id);

    // Looking at this chat right now: nothing to announce (but stop other tabs from beeping).
    if (openConversationId(pathname) === conversationId && document.visibilityState === 'visible' && document.hasFocus()) {
      void claimAlert(messageId, 0);
      return;
    }

    void (async () => {
      const summary = await summaryOf(conversationId);
      if (!summary || summary.muted || summary.snoozed) return;
      const alert: Omit<ChatAlert, 'count' | 'seq'> = {
        conversationId,
        provider: summary.provider,
        providerLabel: summary.providerLabel,
        customerName: summary.customerName,
        accountName: summary.accountName,
        isDemo: summary.isDemo,
        preview: previewText(payload),
        at: event.at,
      };
      seqRef.current += 1;
      const seq = seqRef.current;
      let merged: ChatAlert | undefined;
      if (latest.current.prefs.toast) {
        setAlerts((list) => {
          const next = mergeAlert(list, alert, seq);
          merged = next[0];
          return next;
        });
      }
      if (!(await claimAlert(messageId))) return;
      const current = latest.current.prefs;
      if (current.sound) playChime(current.volume);
      if (current.desktop && document.visibilityState !== 'visible') {
        showDesktopNotification(merged ?? { ...alert, count: 1, seq }, () => open(conversationId));
      }
    })();
  });

  const dismiss = useCallback((conversationId: string) => setAlerts((list) => list.filter((a) => a.conversationId !== conversationId)), []);

  if (!allowed || !prefs.toast || alerts.length === 0) return null;
  const shown = alerts.slice(0, MAX_VISIBLE);
  const hidden = alerts.length - shown.length;

  return createPortal(
    <section className="chat-alerts" aria-label="แชทใหม่" aria-live="polite">
      {shown.map((a) => (
        <ChatAlertCard key={a.conversationId} alert={a} pageVisible={visible} onOpen={open} onDismiss={dismiss} />
      ))}
      {hidden > 0 && (
        <button
          type="button"
          className="chat-alerts__more"
          onClick={() => {
            setAlerts([]);
            navigate('/inbox?view=needs_reply');
          }}
        >
          และอีก {hidden} แชท · เปิดแชทรอตอบ
        </button>
      )}
    </section>,
    document.body,
  );
}

function ChatAlertCard({ alert, pageVisible, onOpen, onDismiss }: { alert: ChatAlert; pageVisible: boolean; onOpen: (id: string) => void; onDismiss: (id: string) => void }) {
  const [paused, setPaused] = useState(false);

  // Auto-dismiss only while the page is visible, restarting on every new message of the chat.
  useEffect(() => {
    if (paused || !pageVisible) return;
    const timer = window.setTimeout(() => onDismiss(alert.conversationId), DISMISS_MS);
    return () => window.clearTimeout(timer);
  }, [paused, pageVisible, alert.seq, alert.conversationId, onDismiss]);

  // A finger can push the card away on a phone or tablet, the same as the close button does.
  const swipe = useSwipeAway(() => onDismiss(alert.conversationId));
  const time = timeFormat.format(new Date(alert.at));
  const label = `แชทใหม่จาก ${alert.customerName} ทาง ${alert.providerLabel} บัญชี ${alert.accountName}${alert.count > 1 ? ` ${alert.count} ข้อความ` : ''}: ${alert.preview}`;
  return (
    <article
      className="chat-alert"
      data-testid="chat-alert"
      {...swipe.handlers}
      style={swipe.style}
      onPointerEnter={() => setPaused(true)}
      onPointerLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      <button type="button" className="chat-alert__open" onClick={() => onOpen(alert.conversationId)} aria-label={`${label} — เปิดแชท`}>
        <ProviderIcon provider={alert.provider} size={36} demoTag={false} />
        <span className="chat-alert__body">
          <span className="chat-alert__top">
            <span className="chat-alert__name truncate">{alert.customerName}</span>
            {alert.count > 1 && <span className="chat-alert__count num">{alert.count} ข้อความ</span>}
          </span>
          <span className="chat-alert__preview">{alert.preview}</span>
          <span className="chat-alert__meta">
            <span className="chat-alert__provider">{alert.providerLabel}</span>
            {alert.isDemo && <span className="provider__demo">DEMO</span>}
            <span aria-hidden="true">·</span>
            <span className="truncate">{alert.accountName}</span>
            <span aria-hidden="true">·</span>
            <time dateTime={alert.at}>{time} น.</time>
          </span>
        </span>
      </button>
      <button type="button" className="chat-alert__close" aria-label="ปิดการแจ้งเตือนแชทนี้" onClick={() => onDismiss(alert.conversationId)}>
        <X size={16} strokeWidth={1.75} aria-hidden="true" />
      </button>
    </article>
  );
}
